import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Eye, Trash2, CheckCircle, MessageSquare } from 'lucide-react';
import { contatoAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function AdminContatos() {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContato, setSelectedContato] = useState(null);
  const { toast } = useToast();

  const fetchContatos = async () => {
    try {
      const res = await contatoAPI.listar();
      setContatos(res.data);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContatos();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await contatoAPI.marcarLido(id);
      toast({ title: "Marcado como lido" });
      fetchContatos();
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    try {
      await contatoAPI.deletar(id);
      toast({ title: "Mensagem excluída com sucesso!" });
      setSelectedContato(null);
      fetchContatos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const getTipoBadge = (tipo) => {
    const badges = {
      contato: 'bg-blue-100 text-blue-700',
      apoiador: 'bg-yellow-100 text-yellow-700',
      voluntario: 'bg-green-100 text-green-700',
      parceria: 'bg-purple-100 text-purple-700',
      outro: 'bg-slate-100 text-slate-700'
    };
    return badges[tipo] || badges.contato;
  };

  return (
    <div data-testid="admin-contatos">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Contatos</h1>
        <p className="text-slate-600 mt-1">Gerencie as mensagens recebidas</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : contatos.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhuma mensagem recebida</h3>
          <p className="text-slate-500">As mensagens de contato aparecerão aqui.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Lista */}
          <div className="lg:col-span-1 space-y-3">
            {contatos.map((contato) => (
              <motion.div
                key={contato.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => setSelectedContato(contato)}
                data-testid={`contato-item-${contato.id}`}
                className={`bg-white rounded-lg p-4 cursor-pointer transition-all ${
                  selectedContato?.id === contato.id
                    ? 'ring-2 ring-blue-500 shadow-md'
                    : 'hover:shadow-md'
                } ${!contato.lido ? 'border-l-4 border-l-blue-500' : ''}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className={`font-medium ${!contato.lido ? 'text-slate-900' : 'text-slate-600'}`}>
                      {contato.nome}
                    </p>
                    <p className="text-sm text-slate-400">{contato.email}</p>
                  </div>
                  {!contato.lido && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </div>
                <p className="text-sm text-slate-500 line-clamp-2">{contato.mensagem}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getTipoBadge(contato.tipo)}`}>
                    {contato.tipo}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(contato.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Detalhes */}
          <div className="lg:col-span-2">
            {selectedContato ? (
              <div className="bg-white rounded-xl p-6" data-testid="contato-detail">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900">
                      {selectedContato.nome}
                    </h2>
                    <p className="text-slate-500">{selectedContato.email}</p>
                    {selectedContato.telefone && (
                      <p className="text-slate-500">{selectedContato.telefone}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!selectedContato.lido && (
                      <button
                        onClick={() => handleMarkAsRead(selectedContato.id)}
                        data-testid="mark-read-btn"
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Marcar como lido"
                      >
                        <CheckCircle size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(selectedContato.id)}
                      data-testid="delete-contato-btn"
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${getTipoBadge(selectedContato.tipo)}`}>
                    {selectedContato.tipo}
                  </span>
                  <span className="text-sm text-slate-400">
                    {new Date(selectedContato.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>

                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-bold text-slate-700 mb-3">Mensagem</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedContato.mensagem}</p>
                </div>

                <div className="mt-6">
                  <a
                    href={`mailto:${selectedContato.email}`}
                    data-testid="reply-btn"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-800 transition-colors"
                  >
                    <Mail size={18} />
                    Responder por E-mail
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center">
                <Mail className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">Selecione uma mensagem</h3>
                <p className="text-slate-500">Clique em uma mensagem para ver os detalhes.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
